import React, { useCallback, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, UploadCloud, FileText, AlertTriangle, CheckCircle2, ArrowRight, Download } from "lucide-react";
import * as XLSX from "xlsx";
import { parseTRDate, parseTRAmount, normalizeSheetDates } from "@/lib/excelDate";

// Beklenen sütun sırası (0-based indeks):
//   A(0)=Mektup No, B(1)=Banka, C(2)=Tür, D(3)=Tutar,
//   E(4)=Veriliş Tarihi, F(5)=Vade Tarihi, G(6)=Lehdar,
//   H(7)=Proje, I(8)=Komisyon %, J(9)=Komisyon Periyodu, K(10)=Notlar
const COLS = {
  letter_number: 0, bank_name: 1, letter_type: 2, amount: 3,
  issue_date: 4, expiry_date: 5, beneficiary_name: 6,
  project: 7, commission_rate: 8, commission_period: 9, notes: 10,
};

const stripApos = (v) => String(v ?? "").replace(/^'+/, "").trim();

function downloadTemplate() {
  const wb = XLSX.utils.book_new();
  const headers = [
    "Mektup No", "Banka", "Tür (Kesin/Geçici/Avans/Performans/Diğer)",
    "Tutar (TL)", "Veriliş Tarihi (GG.AA.YYYY)", "Vade Tarihi (GG.AA.YYYY)",
    "Lehdar", "Proje (opsiyonel)", "Komisyon % (opsiyonel)",
    "Komisyon Periyodu (3 Aylık/6 Aylık/Yıllık/Tek Seferlik)", "Notlar (opsiyonel)",
  ];
  const sample = [
    "TM-2026-0001", "Ziraat Bankası", "Kesin",
    "150000,00", "01.02.2026", "01.02.2027",
    "Karayolları Genel Müdürlüğü", "Yol Yapım İşi", "1,50",
    "3 Aylık", "Örnek kayıt — sil",
  ];
  const ws = XLSX.utils.aoa_to_sheet([headers, sample]);
  ws["!cols"] = headers.map(() => ({ wch: 22 }));
  XLSX.utils.book_append_sheet(wb, ws, "Teminat Mektupları");
  XLSX.writeFile(wb, "teminat-mektup-sablonu.xlsx");
}

export default function GuaranteeImportModal({ onClose, onImported }) {
  const [step, setStep] = useState("upload");
  const [rows, setRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFile = useCallback((file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = new Uint8Array(ev.target.result);
        // raw:true prevents XLSX from auto-interpreting "06.08.2026" TR-format
        // CSV date strings as US MM/DD dates. Excel-native numeric date cells
        // are still converted to TR strings via normalizeSheetDates below.
        const wb = XLSX.read(data, { type: "array", codepage: 65001, raw: true, cellNF: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        normalizeSheetDates(ws);
        const arr = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: "", raw: false, dateNF: "dd.mm.yyyy" });
        if (!arr.length) {
          toast.error("Dosya boş"); return;
        }
        // Başlık satırı algıla
        let startIdx = 0;
        const first = (arr[0] || []).map((x) => String(x || "").toLowerCase());
        if (first.some((c) => c.includes("mektup no") || c.includes("banka") || c.includes("lehdar"))) {
          startIdx = 1;
        }
        const parsed = [];
        for (let i = startIdx; i < arr.length; i++) {
          const r = arr[i];
          const num = stripApos(r[COLS.letter_number]);
          if (!num) continue;
          const bank = String(r[COLS.bank_name] || "").trim();
          const letter_type = String(r[COLS.letter_type] || "Kesin").trim();
          const amountRaw = r[COLS.amount];
          const issueRaw = r[COLS.issue_date];
          const expiryRaw = r[COLS.expiry_date];
          const beneficiary = String(r[COLS.beneficiary_name] || "").trim();
          const amt = parseTRAmount(amountRaw);
          const iso = parseTRDate(issueRaw);
          const exp = parseTRDate(expiryRaw);
          let err = "";
          if (!bank) err = "Banka boş";
          else if (!beneficiary) err = "Lehdar boş";
          else if (!iso) err = "Veriliş tarihi geçersiz";
          else if (!exp) err = "Vade tarihi geçersiz";
          else if (isNaN(amt) || amt <= 0) err = "Tutar geçersiz";
          parsed.push({
            letter_number: num,
            bank_name: bank,
            letter_type,
            amount: isNaN(amt) ? null : amt,
            amount_raw: String(amountRaw || ""),
            issue_date: iso, issue_date_raw: String(issueRaw || ""),
            expiry_date: exp, expiry_date_raw: String(expiryRaw || ""),
            beneficiary_name: beneficiary,
            project: String(r[COLS.project] || "").trim(),
            commission_rate: parseTRAmount(r[COLS.commission_rate]),
            commission_period: String(r[COLS.commission_period] || "3 Aylık").trim(),
            notes: String(r[COLS.notes] || "").trim(),
            _error: err,
          });
        }
        if (!parsed.length) {
          toast.error("Aktarılabilecek satır bulunamadı"); return;
        }
        setRows(parsed);
        setStep("preview");
      } catch (e) {
        console.error(e);
        toast.error("Dosya okunamadı: " + (e?.message || ""));
      }
    };
    reader.onerror = () => toast.error("Dosya okunamadı");
    reader.readAsArrayBuffer(file);
  }, []);

  const stats = useMemo(() => {
    const total = rows.length;
    const invalid = rows.filter((r) => r._error).length;
    const totalAmount = rows.reduce((s, r) => s + (r._error ? 0 : Number(r.amount) || 0), 0);
    return { total, valid: total - invalid, invalid, totalAmount };
  }, [rows]);

  const submit = async () => {
    const items = rows.filter((r) => !r._error).map((r) => ({
      letter_number: r.letter_number,
      bank_name: r.bank_name,
      letter_type: r.letter_type,
      amount: r.amount,
      issue_date: r.issue_date,
      expiry_date: r.expiry_date,
      beneficiary_name: r.beneficiary_name,
      project: r.project || null,
      commission_rate: isNaN(r.commission_rate) ? null : r.commission_rate,
      commission_period: r.commission_period,
      notes: r.notes || null,
    }));
    if (!items.length) { toast.error("Aktarılacak geçerli satır yok"); return; }
    setUploading(true);
    try {
      const { data } = await api.post("/guarantees/bulk-import", { items });
      setResult(data);
      setStep("result");
      toast.success(`${data.imported} teminat mektubu aktarıldı`);
      onImported?.();
    } catch (e) {
      toast.error("Aktarım hatası: " + (e?.response?.data?.detail || e?.message || ""));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="guarantee-import-modal">
      <div className="brutal-card bg-white w-full max-w-5xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between border-b-2 border-black px-5 py-3">
          <div className="flex items-center gap-2">
            <FileText size={18} />
            <h2 className="font-display text-xl font-bold">Teminat Mektuplarını İçe Aktar</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-black/5" data-testid="guarantee-import-close">
            <X size={18} />
          </button>
        </div>

        {step === "upload" && (
          <div className="p-6 space-y-4">
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); }}
              className="border-2 border-dashed border-black/30 p-10 text-center cursor-pointer hover:bg-neutral-50"
              data-testid="guarantee-import-dropzone"
            >
              <UploadCloud size={44} className="mx-auto text-neutral-500" />
              <p className="mt-3 font-semibold">Excel veya CSV dosyası seçin ya da sürükleyin</p>
              <p className="text-xs text-neutral-500 mt-1">.xlsx / .xls / .csv</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="guarantee-import-file"
            />

            <div className="flex items-center gap-2">
              <button
                onClick={downloadTemplate}
                data-testid="guarantee-import-template"
                className="brutal-btn bg-white text-black border border-black px-3 py-1.5 text-xs flex items-center gap-1.5"
              >
                <Download size={12} /> Örnek Şablon İndir (.xlsx)
              </button>
              <span className="text-xs text-neutral-500">Önerilir: önce şablonu indir, doldur, yükle</span>
            </div>

            <div className="bg-neutral-50 border border-black/10 p-4 text-xs">
              <p className="font-semibold uppercase tracking-widest mb-2">Beklenen sütun düzeni (bu sırada)</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-neutral-700">
                <div><b>A</b> — Mektup No</div>
                <div><b>G</b> — Lehdar</div>
                <div><b>B</b> — Banka</div>
                <div><b>H</b> — Proje (ops.)</div>
                <div><b>C</b> — Tür (Kesin/Geçici/Avans/Performans/Diğer)</div>
                <div><b>I</b> — Komisyon % (ops.)</div>
                <div><b>D</b> — Tutar</div>
                <div><b>J</b> — Komisyon Periyodu (3 Aylık/6 Aylık/Yıllık/Tek Seferlik)</div>
                <div><b>E</b> — Veriliş Tarihi</div>
                <div><b>K</b> — Notlar (ops.)</div>
                <div><b>F</b> — Vade Tarihi</div>
                <div />
              </div>
              <p className="text-neutral-500 mt-2">
                Başlık satırı otomatik atlanır. Aynı mektup numaralı kayıt varsa atlanır.
                Türkçe tarih (17.07.2026) ve virgüllü tutar (150000,00) desteklenir.
              </p>
            </div>
          </div>
        )}

        {step === "preview" && (
          <>
            <div className="p-4 border-b border-black/10 text-sm">
              <span className="label-caps mr-2">Okunan:</span>
              <span className="font-mono-num font-semibold">{stats.total}</span>
              <span className="mx-3 text-neutral-300">|</span>
              <span className="label-caps mr-2">Geçerli:</span>
              <span className="font-mono-num font-semibold text-emerald-700">{stats.valid}</span>
              {stats.invalid > 0 && (
                <>
                  <span className="mx-3 text-neutral-300">|</span>
                  <span className="label-caps mr-2">Hatalı:</span>
                  <span className="font-mono-num font-semibold text-rose-600">{stats.invalid}</span>
                </>
              )}
              <span className="mx-3 text-neutral-300">|</span>
              <span className="label-caps mr-2">Toplam Tutar:</span>
              <span className="font-mono-num font-semibold">
                {stats.totalAmount.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
              </span>
            </div>
            <div className="flex-1 overflow-auto">
              <table className="w-full text-xs" data-testid="guarantee-import-preview-table">
                <thead className="bg-neutral-50 sticky top-0 z-10">
                  <tr className="border-b-2 border-black/80">
                    <th className="text-left px-3 py-2 label-caps">Mektup No</th>
                    <th className="text-left px-3 py-2 label-caps">Banka</th>
                    <th className="text-left px-3 py-2 label-caps">Tür</th>
                    <th className="text-right px-3 py-2 label-caps">Tutar</th>
                    <th className="text-left px-3 py-2 label-caps">Veriliş</th>
                    <th className="text-left px-3 py-2 label-caps">Vade</th>
                    <th className="text-left px-3 py-2 label-caps">Lehdar</th>
                    <th className="text-left px-3 py-2 label-caps">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className={`border-b border-black/5 ${r._error ? "bg-rose-50" : ""}`}>
                      <td className="px-3 py-1.5 font-mono-num">{r.letter_number}</td>
                      <td className="px-3 py-1.5">{r.bank_name || "—"}</td>
                      <td className="px-3 py-1.5">{r.letter_type}</td>
                      <td className="px-3 py-1.5 text-right font-mono-num">
                        {r.amount != null
                          ? Number(r.amount).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : <span className="text-rose-600">{r.amount_raw}</span>}
                      </td>
                      <td className="px-3 py-1.5 font-mono-num">{r.issue_date || <span className="text-rose-600">{r.issue_date_raw}</span>}</td>
                      <td className="px-3 py-1.5 font-mono-num">{r.expiry_date || <span className="text-rose-600">{r.expiry_date_raw}</span>}</td>
                      <td className="px-3 py-1.5">{r.beneficiary_name || "—"}</td>
                      <td className="px-3 py-1.5">
                        {r._error
                          ? <span className="text-rose-700 text-[10px] uppercase tracking-widest flex items-center gap-1"><AlertTriangle size={10} /> {r._error}</span>
                          : <span className="text-emerald-700 text-[10px] uppercase tracking-widest">Hazır</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t border-black/10 p-4 flex items-center justify-between gap-2">
              <button
                onClick={() => { setRows([]); setStep("upload"); }}
                className="brutal-btn bg-white text-black border border-black px-4 py-2 text-sm"
                data-testid="guarantee-import-back"
              >
                Geri
              </button>
              <button
                onClick={submit}
                disabled={uploading || stats.valid === 0}
                className="brutal-btn bg-black text-white px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-40"
                data-testid="guarantee-import-submit"
              >
                {uploading ? "Aktarılıyor…" : `${stats.valid} Kaydı Aktar`}
                <ArrowRight size={14} />
              </button>
            </div>
          </>
        )}

        {step === "result" && result && (
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={40} className="text-emerald-600" />
              <div>
                <h3 className="font-display text-2xl font-bold">Aktarım Tamamlandı</h3>
                <p className="text-sm text-neutral-600">Sonuç aşağıda özetlenmiştir.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <RStat label="Aktarılan" value={result.imported} tone="emerald" />
              <RStat label="Mükerrer" value={result.skipped_duplicates} />
              <RStat label="Cari Eşleşen" value={result.matched_contacts} tone="emerald" />
              <RStat label="Hatalı" value={result.error_count} tone={result.error_count > 0 ? "rose" : ""} />
            </div>
            {result.errors && result.errors.length > 0 && (
              <div className="max-h-56 overflow-auto border border-rose-200 bg-rose-50 p-3">
                <p className="font-semibold text-xs uppercase tracking-widest text-rose-700 mb-2">Hatalar</p>
                <ul className="text-xs space-y-1 text-rose-800">
                  {result.errors.map((e, i) => <li key={i}>Satır {e.row}: {e.message}</li>)}
                </ul>
              </div>
            )}
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="brutal-btn bg-black text-white px-4 py-2 text-sm"
                data-testid="guarantee-import-done"
              >
                Kapat
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RStat({ label, value, tone }) {
  const cls = tone === "emerald" ? "text-emerald-700" : tone === "rose" ? "text-rose-600" : "text-black";
  return (
    <div className="border-2 border-black p-3">
      <p className="label-caps text-[10px]">{label}</p>
      <p className={`font-mono-num text-2xl font-bold mt-1 ${cls}`}>{value ?? 0}</p>
    </div>
  );
}
